/**
 * A speaker's monogram — the same object as `components/ui/avatar`, with one
 * difference that matters on these two screens.
 *
 * `SpeakerAvatar` derives its initials from a HUMAN name: `Ana Ruiz` → `AR`,
 * and a single word is cut to its first two characters. That is right for a
 * call, where every name came from a person typing it into the lobby.
 *
 * The history and session screens also render people who have no human name at
 * all — a session this app did not create has only the API's identities, and
 * upstream never stored anything else (FINDINGS.md #10). Under the shared rule
 * `u_a` and `u_b` both become **`U_`**, so the two speakers in the reference
 * session are distinguishable only by colour, which is exactly the
 * colour-is-the-only-channel failure the rest of this screen works to avoid.
 *
 * So the initials here split on the separators identities actually use.
 * `u_a` → `UA`, `u_9fA3kQ` → `U9`, `Ana Ruiz` → `AR` — the human case is
 * unchanged. `components/ui` belongs to the whole app and is not this
 * feature's to redefine, which is why this is a local component rather than an
 * edit to the shared one.
 */

import type * as React from 'react'
import { cn } from '@/lib/utils'
import { speakerColorVar } from '@/lib/format'

const SIZES = {
  sm: 'size-7 text-[0.6875rem]',
  md: 'size-9 text-xs',
  lg: 'size-12 text-sm',
} as const

export function Monogram({
  displayName,
  index,
  size = 'md',
  className,
  style,
}: {
  displayName: string
  /** Position in the roster — the only input to the colour. Never a hash. */
  index: number
  size?: keyof typeof SIZES
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white select-none',
        SIZES[size],
        className,
      )}
      style={{ backgroundColor: speakerColorVar(index), ...style }}
    >
      {initials(displayName)}
    </span>
  )
}

export function initials(name: string): string {
  // Split on whitespace, underscores, dots and dashes — the separators that
  // appear in both `Ana Ruiz` and `u_9fA3kQ`.
  const parts = name
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}
